import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../tailwindcss/Tailwind.css';
import Chart from 'chart.js/auto';


import DataSourceSelector from './components/DataSourceSelector';

import segmentationService from './api';
import { buildApiUrl } from "../../services/apiConfig";

const CustomerSegmentation = () => {
  const navigate = useNavigate();

  const [dataSource, setDataSource] = useState('file');
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('No file selected');
  const [sqlQuery, setSqlQuery] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [error, setError] = useState('');

  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [saveDir, setSaveDir] = useState(null);

  const [previousAnalyses, setPreviousAnalyses] = useState([]);
  const [isLoadingAnalyses, setIsLoadingAnalyses] = useState(true);

  const [dateRange, setDateRange] = useState('7d');
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [customEndDate, setCustomEndDate] = useState(new Date().toISOString().split('T')[0]);

  const [activeTab, setActiveTab] = useState('segmentation');

  const [summaryMetrics, setSummaryMetrics] = useState({
    totalRecords: 0,
    clusters: 0,
    silhouetteScore: 0,
    anomalies: 0
  });

  const clusterChartRef = React.useRef(null);
  const anomalyChartRef = React.useRef(null);

  const [globalThemes, setGlobalThemes] = useState({});
  const [globalClusterSizes, setGlobalClusterSizes] = useState({});
  const [selectedCluster, setSelectedCluster] = useState(null);

  useEffect(() => {
    loadPreviousAnalyses();
  }, []);

  useEffect(() => {
    return () => {
      if (clusterChartRef.current) {
        clusterChartRef.current.destroy();
      }
      if (anomalyChartRef.current) {
        anomalyChartRef.current.destroy();
      }
    };
  }, []);

  const loadPreviousAnalyses = async () => {
    setIsLoadingAnalyses(true);
    try {
      const data = await segmentationService.getAnalyses();
      setPreviousAnalyses(data.analyses || []);
    } catch (error) {
      console.error("History fetch error:", error);
    } finally {
      setIsLoadingAnalyses(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFileName(selectedFile.name);
      setError('');
    } else {
      setFile(null);
      setFileName('No file selected');
    }
  };

  const handleUploadClick = () => {
    document.getElementById('file-upload').click();
  };

  const handleAnalyze = async () => {
    setError('');

    if (dataSource === 'file') {
      if (!file) {
        setError("Please select a dataset file first.");
        return;
      }
      await uploadFile();
    } else if (dataSource === 'sql') {
      if (!sqlQuery.trim()) {
        setError("Please enter a SQL query first.");
        return;
      }
      await uploadSqlQuery();
    }
  };

  const uploadFile = async () => {
    setIsProcessing(true);
    setUploadStatus("Processing dataset... This may take a moment.");

    try {
      const params = apiKey ? { openai_key: apiKey } : {};
      const result = await segmentationService.uploadFile(file, params);

      setUploadStatus("Analysis Complete");
      setSaveDir(result.save_dir);
      setAnalysisComplete(true);

      loadResults(result.save_dir);

      loadPreviousAnalyses();
    } catch (error) {
      setUploadStatus(`Error: ${error.message}`);
      setError(`Error: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const uploadSqlQuery = async () => {
    setIsProcessing(true);
    setUploadStatus("Processing SQL query... This may take a moment.");

    try {
      const params = apiKey ? { openai_key: apiKey } : {};

      const response = await fetch(buildApiUrl('CLUSTER', '/cluster/upload-sql'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: sqlQuery,
          params: params
        }),
      });

      if (!response.ok) {
        throw new Error(`SQL query failed with status: ${response.status}`);
      }

      const result = await response.json();

      setUploadStatus("Analysis Complete");
      setSaveDir(result.save_dir);
      setAnalysisComplete(true);

      loadResults(result.save_dir);

      loadPreviousAnalyses();
    } catch (error) {
      setUploadStatus(`Error: ${error.message}`);
      setError(`Error: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const viewAnalysis = async (save_dir) => {
    try {
      const result = await segmentationService.loadAnalysis(save_dir);
      if (result.status === 'success') {
        setSaveDir(save_dir);
        setAnalysisComplete(true);
        loadResults(save_dir);
      } else {
        alert('Error: ' + result.message);
      }
    } catch (error) {
      console.error("Analysis load error:", error);
      alert('Connection failure while loading analysis.');
    }
  };

  const deleteAnalysis = async (save_dir) => {
    if (!window.confirm('Permanently delete this analysis?')) return;

    try {
      const result = await segmentationService.deleteAnalysis(save_dir);
      if (result.status === 'success') {
        loadPreviousAnalyses();
      } else {
        alert('Deletion failed: ' + result.message);
      }
    } catch (error) {
      alert('Error communicating with server.');
    }
  };

  const loadResults = async (save_dir) => {
    try {
      const [summary, themes, anomalies, meta] = await Promise.all([
        segmentationService.getSummaryStatistics(save_dir),
        segmentationService.getClusterThemes(save_dir).catch(() => ({})),
        segmentationService.getAnomalyExplanations(save_dir).catch(() => ({ items: [], total_outliers: 0 })),
        segmentationService.getMetadata(save_dir).catch(() => null)
      ]);

      setGlobalThemes(themes);
      setGlobalClusterSizes(summary.cluster_sizes || {});

      setSummaryMetrics({
        totalRecords: summary.total_records || 0,
        clusters: summary.n_clusters || 0,
        silhouetteScore: (summary.silhouette_score || 0).toFixed(4),
        anomalies: summary.final_true_anomalies || 0
      });

      renderCharts(summary.cluster_sizes || {}, anomalies.by_cluster_counts || {}, themes, save_dir);

    } catch (error) {
      console.error("Result load error:", error);
      alert("Error loading analysis results.");
    }
  };


  const renderCharts = (clusterSizes, anomalyCounts, themes, save_dir) => {
    setTimeout(() => {
      renderBarCharts('clusterChart', 'anomalyChart', clusterSizes, anomalyCounts, themes);
    }, 100);
  };

  const renderBarCharts = (canvasId1, canvasId2, data1, data2, themes) => {
    const el1 = document.getElementById(canvasId1);
    const el2 = document.getElementById(canvasId2);

    if (!el1 || !el2) return;

    const getThemeLabel = (k) => {
      const t = themes[k];
      let title = t?.intelligent_label || t?.theme_title || t?.theme || `Cluster ${k}`;
      return `C${k}: ${title.toUpperCase()}`;
    };

    const chartConfig = (data, label, isBlue, keys) => {
      const values = keys.map(k => data[k] || 0);

      return {
        type: 'bar',
        data: {
          labels: keys.map(k => {
            const lbl = getThemeLabel(k);
            return lbl.substring(0, 35) + (lbl.length > 35 ? '...' : '');
          }),
          datasets: [{
            label: label,
            data: values,
            backgroundColor: isBlue ? '#3B82F6' : '#EF4444',
            hoverBackgroundColor: isBlue ? '#2563EB' : '#DC2626',
            borderWidth: 0,
            borderColor: 'transparent',
            borderRadius: 4,
            barPercentage: 0.7,
            categoryPercentage: 0.8
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          indexAxis: 'x',
          animation: {
            duration: 500,
            easing: 'easeOutQuart'
          },
          plugins: {
            legend: {
              display: true,
              position: 'top',
              labels: {
                font: { size: 12, weight: '600' },
                color: '#333',
                padding: 15
              }
            },
            tooltip: {
              backgroundColor: isBlue ? 'rgba(37, 99, 235, 0.95)' : 'rgba(220, 38, 38, 0.95)',
              padding: 12,
              titleColor: '#fff',
              bodyColor: '#fff',
              callbacks: {
                title: (items) => getThemeLabel(keys[items[0].dataIndex]),
                label: (context) => `${label}: ${context.parsed.y.toLocaleString()}`
              }
            }
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: {
                font: { size: 11, weight: '500' },
                color: '#555',
                maxRotation: 45,
                minRotation: 0
              }
            },
            y: {
              beginAtZero: true,
              grid: {
                color: '#f1f5f9',
                lineWidth: 1
              },
              ticks: {
                font: { size: 11, weight: '500' },
                color: '#666'
              }
            }
          },
          onClick: (e, elements) => {
            if (elements.length > 0) {
              const index = elements[0].index;
              const clusterId = keys[index];
              setSelectedCluster(clusterId);
            }
          }
        }
      };
    };

    if (el1) {
      try {
        if (clusterChartRef.current) {
          clusterChartRef.current.destroy();
        }

        const ctx1 = el1.getContext('2d');
        const chartData1 = data1 && Object.keys(data1).length > 0 ? data1 : {};
        const keys1 = Object.keys(chartData1).sort((a, b) => parseInt(a) - parseInt(b));

        if (keys1.length > 0) {
          clusterChartRef.current = new Chart(ctx1, chartConfig(chartData1, 'Cluster Sizes', true, keys1));
        } else {
          clusterChartRef.current = new Chart(ctx1, {
            type: 'bar',
            data: {
              labels: ['No data'],
              datasets: [{
                label: 'Cluster Sizes',
                data: [0],
                backgroundColor: '#3B82F6'
              }]
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: { legend: { display: true }, tooltip: { enabled: false } },
              scales: { y: { beginAtZero: true } }
            }
          });
        }
      } catch (e) {
        console.error('Error rendering cluster chart:', e);
      }
    }

    if (el2) {
      try {
        if (anomalyChartRef.current) {
          anomalyChartRef.current.destroy();
        }

        const ctx2 = el2.getContext('2d');
        const chartData2 = data2 && Object.keys(data2).length > 0 ? data2 : {};
        const keys2 = Object.keys(chartData2).sort((a, b) => parseInt(a) - parseInt(b));

        if (keys2.length > 0) {
          anomalyChartRef.current = new Chart(ctx2, chartConfig(chartData2, 'Validated Anomalies', false, keys2));
        } else {
          anomalyChartRef.current = new Chart(ctx2, {
            type: 'bar',
            data: {
              labels: ['No data'],
              datasets: [{
                label: 'Validated Anomalies',
                data: [0],
                backgroundColor: '#EF4444'
              }]
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: { legend: { display: true }, tooltip: { enabled: false } },
              scales: { y: { beginAtZero: true } }
            }
          });
        }
      } catch (e) {
        console.error('Error rendering anomaly chart:', e);
      }
    }
  };

  const closeClusterModal = () => {
    setSelectedCluster(null);
  };


  return (
    <div className="flex bg-gray-50">
      <main className="w-full flex-1 p-6 overflow-x-hidden">
        <div className="w-full">
          

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
              <div className="text-sm text-gray-500 font-medium">Total Records</div>
              <div className="text-2xl text-primary">{summaryMetrics.totalRecords.toLocaleString()}</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
              <div className="text-sm text-gray-500 font-medium">Clusters Identified</div>
              <div className="text-2xl text-primary">{summaryMetrics.clusters}</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
              <div className="text-sm text-gray-500 font-medium">Silhouette Score</div>
              <div className="text-2xl text-primary">{summaryMetrics.silhouetteScore}</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
              <div className="text-sm text-gray-500 font-medium">High-Signal Anomalies</div>
              <div className="text-2xl text-primary">{summaryMetrics.anomalies}</div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 pb-0 mb-6">

            <DataSourceSelector
              dataSource={dataSource}
              setDataSource={setDataSource}
              file={file}
              fileName={fileName}
              sqlQuery={sqlQuery}
              setSqlQuery={setSqlQuery}
              handleFileChange={handleFileChange}
              handleUploadClick={handleUploadClick}
              handleAnalyze={handleAnalyze}
              isAnalyzing={isProcessing}
              error={error}
            />

            {uploadStatus && (
              <p className={`text-center mt-3 text-sm font-medium ${uploadStatus.includes('Error') ? 'text-red-500' :
                uploadStatus === 'Analysis Complete' ? 'text-green-500' : 'text-primary'
                }`}>
                {uploadStatus}
              </p>
            )}
          </div>

          {analysisComplete && (
            <>
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 mb-6">
                <div className="text-lg font-medium mb-4">Cluster Composition</div>
                <div className="h-80">
                  <canvas id="clusterChart"></canvas>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 mb-6">
                <div className="text-lg font-medium mb-4">Anomaly Distribution</div>
                <div className="h-80">
                  <canvas id="anomalyChart"></canvas>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 mb-6">
                <div className="text-lg font-medium mb-4">Semantic Themes</div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.keys(globalThemes).length > 0 ? (
                    Object.keys(globalThemes).sort((a, b) => parseInt(a) - parseInt(b)).map(id => {
                      const theme = globalThemes[id];
                      const clusterSize = globalClusterSizes[id] || 0;

                      return (
                        <div
                          key={id}
                          className="border border-gray-100 rounded-lg p-4 hover:shadow-md transition-all cursor-pointer"
                          onClick={() => setSelectedCluster(id)}
                        >
                          <div className="flex justify-between items-start mb-3">
                            <span className="bg-primary text-white px-2 py-1 rounded-md text-xs font-medium">CLUSTER #{id}</span>
                            <span className="text-xs text-gray-500">{clusterSize} ITEMS</span>
                          </div>
                          <div className="font-medium text-gray-800 mb-2">
                            {theme.intelligent_label || theme.theme_title || theme.theme || `Cluster ${id}`}
                          </div>
                          <p className="text-xs text-gray-600 mb-3 line-clamp-3">
                            {theme.summary || "No automated summary available for this cluster profile."}
                          </p>
                          <div>
                            <div className="text-xs text-gray-500 mb-1">Key Identifiers</div>
                            <div className="flex flex-wrap gap-1">
                              {(Array.isArray(theme.top_terms) ? theme.top_terms : [])
                                .slice(0, 4)
                                .map((term, idx) => (
                                  <span
                                    key={idx}
                                    className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs"
                                  >
                                    {term.term || term}
                                  </span>
                                ))
                              }
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-center w-full text-gray-500 col-span-3 py-6">No thematic patterns identified.</p>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 mb-6">
                <div className="text-lg font-medium mb-4">Technical Findings</div>
                <pre className="bg-gray-50 text-gray-700 p-4 rounded-md overflow-x-auto text-xs">
                  {`TIMESTAMP: ${new Date().toLocaleString()}
ANALYSIS ID: ${saveDir}
SELECTED MODEL: Neural Clustering
SAMPLING RATE: 100%
DIMENSIONALITY: ${summaryMetrics.totalRecords > 0 ? 'Auto-detected' : 'N/A'} reduced to 10
ANOMALY THRESHOLD: 0.85
CLUSTERS GENERATED: ${summaryMetrics.clusters}`}
                </pre>
              </div>
            </>
          )}

          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
            <div className="text-lg font-medium mb-4">Previous Analyses</div>

            {isLoadingAnalyses ? (
              <div className="flex justify-center items-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              </div>
            ) : previousAnalyses.length === 0 ? (
              <p className="text-center text-gray-500 py-6 text-sm">No archived analyses found. Launch your first one above!</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {previousAnalyses.map((analysis) => {
                  const date = new Date(analysis.modified_time).toLocaleDateString();
                  const time = new Date(analysis.modified_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

                  return (
                    <div key={analysis.save_dir} className="border border-gray-100 rounded-lg p-4 hover:border-gray-200 hover:shadow-sm transition-all">
                      <div className="flex justify-between items-start mb-3">
                        <span className="font-medium text-gray-800 truncate">{analysis.dataset_name}</span>
                        <span className="text-xs text-gray-500">{date}</span>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
                        <div className="text-center bg-gray-50 p-2 rounded min-w-0">
                          <div className="font-medium text-primary text-sm">{analysis.total_records}</div>
                          <div className="text-xs text-gray-500">Records</div>
                        </div>
                        <div className="text-center bg-gray-50 p-2 rounded min-w-0">
                          <div className="font-medium text-primary text-sm">{analysis.n_clusters}</div>
                          <div className="text-xs text-gray-500">Clusters</div>
                        </div>
                        <div className="text-center bg-gray-50 p-2 rounded min-w-0">
                          <div className="font-medium text-primary text-sm">{analysis.final_true_anomalies}</div>
                          <div className="text-xs text-gray-500">Anomalies</div>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => viewAnalysis(analysis.save_dir)}
                          className="flex-grow py-1 px-3 bg-primary text-white rounded text-xs font-medium hover:bg-primaryDark transition-all"
                        >
                          View Results
                        </button>
                        <button
                          onClick={() => deleteAnalysis(analysis.save_dir)}
                          className="py-1 px-3 bg-gray-100 text-gray-600 rounded text-xs font-medium hover:bg-gray-200 transition-all"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>

      {selectedCluster && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-lg">
            <div className="flex justify-between items-center border-b border-gray-100 p-4">
              <div className="flex items-center">
                <span className="bg-primary text-white px-2 py-1 rounded text-xs font-medium mr-2">
                  CLUSTER #{selectedCluster}
                </span>
                <span className="text-gray-500 text-xs">
                  {globalClusterSizes[selectedCluster] || 0} Records
                </span>
              </div>
              <button
                onClick={closeClusterModal}
                className="text-gray-400 hover:text-gray-600 transition-all"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4">
              {globalThemes[selectedCluster] && (
                <>
                  <h2 className="text-xl font-medium text-gray-800 mb-3">
                    {globalThemes[selectedCluster].intelligent_label ||
                      globalThemes[selectedCluster].theme_title ||
                      globalThemes[selectedCluster].theme ||
                      `Cluster ${selectedCluster}`}
                  </h2>

                  <div className="bg-gray-50 p-3 rounded mb-4">
                    <h4 className="text-xs text-gray-500 uppercase mb-1">Summary</h4>
                    <p className="text-sm text-gray-700">
                      {globalThemes[selectedCluster].summary || 'No automated summary available for this cluster profile.'}
                    </p>
                  </div>

                  {globalThemes[selectedCluster].why && (
                    <div className="bg-gray-50 p-3 rounded mb-4">
                      <h4 className="text-xs text-gray-500 uppercase mb-1">Why This Cluster?</h4>
                      <p className="text-sm text-gray-700">
                        {globalThemes[selectedCluster].why}
                      </p>
                    </div>
                  )}

                  <div className="mb-4">
                    <h4 className="text-xs text-gray-500 uppercase mb-1">Key Identifiers & Terms</h4>
                    <div className="flex flex-wrap gap-1">
                      {(Array.isArray(globalThemes[selectedCluster].top_terms) ?
                        globalThemes[selectedCluster].top_terms : []
                      ).map((term, idx) => (
                        <span
                          key={idx}
                          className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs"
                        >
                          {term.term || term}
                        </span>
                      ))}
                    </div>
                  </div>

                  {globalThemes[selectedCluster].exemplar_texts &&
                    Array.isArray(globalThemes[selectedCluster].exemplar_texts) &&
                    globalThemes[selectedCluster].exemplar_texts.length > 0 && (
                      <div>
                        <h4 className="text-xs text-gray-500 uppercase mb-1">Sample Records</h4>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {globalThemes[selectedCluster].exemplar_texts.slice(0, 3).map((text, idx) => (
                            <div
                              key={idx}
                              className="bg-gray-50 border-l-2 border-primary p-3 rounded text-xs"
                            >
                              <div className="text-xs text-gray-500 mb-1">Example {idx + 1}</div>
                              <div className="text-gray-700">
                                {(text || '').substring(0, 300)}
                                {(text || '').length > 300 ? '...' : ''}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                </>
              )}
            </div>

            <div className="border-t border-gray-100 p-3 flex justify-end">
              <button
                onClick={closeClusterModal}
                className="px-3 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium hover:bg-gray-200 transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerSegmentation;